'use client';

import { Controller, useForm } from 'react-hook-form';
import { lazy, type ReactNode, type SubmitEventHandler, Suspense, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useIntl } from 'react-intl';
import { Input } from '@packages/shared/src/ui/Input/Input';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Checkbox } from '@packages/shared/src/ui/Checkbox/Checkbox';
import { Label } from '@packages/shared/src/ui/Label/Label';
import { z } from 'zod';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { Card } from '@packages/shared/src/ui/Card/Card';
import { InputPassword } from '@packages/shared/src/ui/Input/InputPassword/InputPassword.client';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { GTMEventGlycerinFormSubmitClick } from '@packages/tracking/src/types/events';
import { useLogin } from '../api/useLogin';
import { messages } from './messages';
import { Link } from '../../Link/Link';
import { ForgottenPasswordButton } from '../ForgottenPassword/ForgottenPasswordButton';
import { parseBackendMessage } from '../../BackendMessageParser/BackendMessageParser';

const ActivateEmailLoginModal = lazy(() =>
  import('../ActivateEmailLogin/ActivateEmailLoginModal.client').then((mod) => ({
    default: mod.ActivateEmailLoginModal,
  })),
);

const iconStylingTailwind = 'w-4 h-4 md:w-3 md:h-3 text-text-dark';

type LoginFormProps = {
  onSuccess?: () => void;
  className?: string;
  children?: ReactNode;
  /**
   * Pre-fill the email field with this value.
   * Used by the check variant after the email-check step confirms the email is known.
   */
  prefillEmail?: string;
  /**
   * Called when the user clicks the edit icon next to the pre-filled email.
   * Only shown when prefillEmail is set.
   */
  onEditEmail?: () => void;
  /**
   * Render the action bar below the form (back button + submit).
   * Receives the pre-built submit button so the caller only needs to supply
   * the layout / back button. Ignored when `children` is provided.
   */
  renderActions?: (submitButton: ReactNode) => ReactNode;
  /** Optional text rendered inside the grey area at the top (used by check variant to show contextual heading). */
  subline?: ReactNode;
};

export const LoginForm = ({
  onSuccess,
  className,
  children,
  prefillEmail,
  onEditEmail,
  renderActions,
  subline,
}: LoginFormProps) => {
  const { formatMessage } = useIntl();
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const { login, error, loading } = useLogin(() => onSuccess && onSuccess());
  const dispatchEvent = useTracking();

  const userSchema = z.object({
    email: z
      .string()
      .nonempty(formatMessage(messages.errorMandatoryField))
      .check(z.email(formatMessage(messages.errorInvalidMail))),
    password: z
      .string()
      .nonempty(formatMessage(messages.errorMandatoryField))
      .min(6, formatMessage(messages.errorTooShort))
      .max(256, formatMessage(messages.errorTooLong)),
    remember: z.boolean().optional(),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isLoading, isSubmitting, touchedFields },
  } = useForm<z.infer<typeof userSchema>>({
    resolver: zodResolver(userSchema),
    mode: 'onChange',
    defaultValues: {
      email: prefillEmail ?? '',
    },
  });

  const msg = !error
    ? null
    : parseBackendMessage(error, {
        break: <br />,
        maillink: (content) => <Link href={`mailto:${content}`}>{content}</Link>,
        migrate: (content) => (
          <Button
            variant="textLink"
            className="hover:text-color-primary-light cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setShowActivateDialog(true);
            }}
          >
            {content}
          </Button>
        ),
      });

  const submitButton = (
    <Button
      data-testid="submit-button"
      color="primary"
      type="submit"
      className="w-full justify-center sm:w-auto"
    >
      {formatMessage(messages.login)}
    </Button>
  );

  const onSuccessfulSubmit = (values: z.infer<typeof userSchema>) => {
    login(values);
  };

  const onFormSubmit: SubmitEventHandler<HTMLFormElement> = (e) => {
    dispatchEvent<GTMEventGlycerinFormSubmitClick>({
      event: 'FormSubmitClick',
      FormSubmitClickData: {
        category: 'login formular',
        detail: 'email',
      },
    });
    handleSubmit(onSuccessfulSubmit)(e);
  };

  return (
    <form onSubmit={onFormSubmit} className="flex flex-col">
      <div className={cn('bg-color-grey-light relative w-full', className)}>
        {subline && <div className="px-2 pt-2 text-center">{subline}</div>}
        {(isSubmitting || loading || isLoading) && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-[#fff]/50">
            <CircularProgress className={iconStylingTailwind} />
          </div>
        )}
        {error && (
          <Notification variant="outline" className="mx-2 mt-2 w-auto" severity="error">
            {msg}
          </Notification>
        )}
        {showActivateDialog && (
          <Suspense>
            <ActivateEmailLoginModal
              modalOpen={showActivateDialog}
              onClose={() => setShowActivateDialog(false)}
            />
          </Suspense>
        )}
        <div className="text-size-body-xs ml-2 pt-2">{formatMessage(messages.requiredFields)}</div>
        <Card className="m-2 flex flex-col gap-3 p-2 text-left">
          {/* Email field */}
          <div className="flex items-center gap-1">
            <div className="flex-1">
              <Input
                label={formatMessage(messages.emailLabel)}
                placeholder={formatMessage(messages.emailPlaceholder)}
                type="email"
                role="textbox"
                error={!!errors?.email && !!touchedFields.email}
                helperText={
                  errors?.email && !!touchedFields.email ? errors.email?.message : undefined
                }
                className="rounded-sm"
                disabled={!!prefillEmail}
                {...register('email', { required: 'Required' })}
              />
            </div>
            {prefillEmail && onEditEmail && (
              <button
                type="button"
                onClick={onEditEmail}
                aria-label={formatMessage(messages.editEmailAriaLabel)}
                className="text-color-text-dark mt-3 shrink-0 p-1"
              >
                <span className="icon-emp-edit size-4" />
              </button>
            )}
          </div>

          {/* Password field */}
          <div>
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, onBlur, value } }) => (
                <InputPassword
                  showPasswordButtonLabelShow={formatMessage(messages.showPasswordButtonLabelShow)}
                  showPasswordButtonLabelHide={formatMessage(messages.showPasswordButtonLabelHide)}
                  charsCounterLabel={formatMessage(messages.passwordCounterLabel)}
                  label={formatMessage(messages.passwordLabel)}
                  placeholder={formatMessage(messages.passwordPlaceholder)}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  type="password"
                  error={!!errors?.password && !!touchedFields.password}
                  helperText={
                    errors?.password && !!touchedFields.password
                      ? errors.password?.message
                      : undefined
                  }
                />
              )}
            />
            <ForgottenPasswordButton />
          </div>

          <div className="flex items-center" data-testid="newsletter-checkbox">
            <Controller
              control={control}
              name="remember"
              render={({ field: { onChange, onBlur, value } }) => (
                <Checkbox id="remember" onChange={onChange} onBlur={onBlur} checked={value} />
              )}
            />
            <Label htmlFor="stayLoggedIn">{formatMessage(messages.stayLoggedIn)}</Label>
          </div>
        </Card>
      </div>
      {children ??
        (renderActions ? (
          renderActions(submitButton)
        ) : (
          <div className="mt-2 flex justify-end px-1.5">{submitButton}</div>
        ))}
    </form>
  );
};
